import React, { useState } from "react";
import { useNotification } from "../pages/shared/NotificationProvider";
import { TranslationService } from "../services/TranslationService";
import { BrowserNotificationService } from "../services/BrowserNotificationService";

interface NotificationPermissionButtonProps {
  className?: string;
  variant?: "button" | "link" | "badge";
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

/**
 * Componente para solicitar permisos de notificaciones al usuario
 * Muestra un botón que permite al usuario habilitar notificaciones
 */
export const NotificationPermissionButton: React.FC<
  NotificationPermissionButtonProps
> = ({
  className = "",
  variant = "button",
  onPermissionGranted,
  onPermissionDenied
}) => {
  const { permission, isSupported, requestPermission } = useNotification();
  const [isRequesting, setIsRequesting] = useState(false);

  // Si no está soportado, no mostrar nada
  if (!isSupported) {
    return null;
  }

  // Si ya tiene permisos, no mostrar el botón
  if (permission === "granted") {
    return null;
  }

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const result = await requestPermission();
      if (result === "granted") {
        onPermissionGranted?.();
        await BrowserNotificationService.showPermissionGrantedNotification();
      } else {
        onPermissionDenied?.();
      }
    } catch (error) {
      console.error("Error al solicitar permisos:", error);
      onPermissionDenied?.();
    } finally {
      setIsRequesting(false);
    }
  };

  if (variant === "link") {
    return (
      <button
        type="button"
        className={`btn btn-link p-0 ${className}`}
        onClick={handleRequestPermission}
        disabled={isRequesting}
      >
        {isRequesting
          ? TranslationService.translate?.NotificationsEnabling ||
            "Habilitando..."
          : TranslationService.translate?.EnableNotifications ||
            "Habilitar notificaciones"}
      </button>
    );
  }

  if (variant === "badge") {
    return (
      <span
        className={`badge bg-warning text-dark cursor-pointer ${className}`}
        onClick={handleRequestPermission}
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleRequestPermission();
          }
        }}
      >
        {isRequesting
          ? TranslationService.translate?.NotificationsEnabling ||
            "Habilitando..."
          : TranslationService.translate?.EnableNotifications ||
            "Habilitar notificaciones"}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`btn btn-primary ${className}`}
      onClick={handleRequestPermission}
      disabled={isRequesting}
    >
      <i className="fa fa-bell me-2"></i>
      {isRequesting
        ? TranslationService.translate?.NotificationsEnabling ||
          "Habilitando..."
        : TranslationService.translate?.EnableNotifications ||
          "Habilitar notificaciones"}
    </button>
  );
};

export default NotificationPermissionButton;
